// @ts-strict-ignore
import moment from 'moment';
import { useEffect } from 'react';
import { usePrevious } from '@shared/util/hooks';

/**
 * This hook ensures date range logic is applied to a set of dates
 * by observing their changes and reconciling via their setter functions.
 *
 * 1) If either date is cleared, clear both
 * 2) If startDate changes, and endDate is either not set or is less than startDate, set endDate to startDate + unitOfTime
 * 3) If endDate changes, and startDate is either not set or is greater than endDate, set startDate to endDate - unitOfTime
 */
export function useDateRangeDefaultValues(
  startDate: string,
  setStartDate: (d: string) => void,
  endDate: string,
  setEndDate: (d: string) => void,
  unit: string,
  count: number,
): void {
  const prevStartDate = usePrevious(startDate);
  const prevEndDate = usePrevious(endDate);

  useEffect(() => {
    if ((prevStartDate && !startDate) || (prevEndDate && !endDate)) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    if (startDate !== prevStartDate && (!endDate || endDate < startDate)) {
      setEndDate(
        moment(startDate)
          .startOf(unit as moment.unitOfTime.StartOf)
          .add(count, unit as moment.unitOfTime.DurationAs)
          .toISOString(),
      );
      return;
    }

    if (endDate !== prevEndDate && (!startDate || startDate > endDate)) {
      setStartDate(
        moment(endDate)
          .startOf(unit as moment.unitOfTime.StartOf)
          .subtract(count, unit as moment.unitOfTime.DurationAs)
          .toISOString(),
      );
      return;
    }
  }, [startDate, endDate, prevStartDate, prevEndDate, setStartDate, setEndDate, count, unit]);
}
